import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  DocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { classNames } from 'support/helpers/generic/generic';
import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { translationForDocumentTypeWithVirtual } from 'support/helpers/transactions/transactions';
import {
  DocumentType,
  MessageDTO,
  PartnerDTO,
  TransactionInternalType,
  TransactionState,
  ValueOf,
} from 'support/types';
import { InProgresCircle } from 'components/CustomIcons/InProgressCircle';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';

type RelatedMessageType = {
  id: string;
  type?: ValueOf<DocumentType> | null;
  externalMessageRef?: string | null;
  createdAt: string;
  sender?: PartnerDTO;
  receiver?: PartnerDTO;
  state: MessageDTO['state'];
};

type RelatedMessageProps = {
  relatedMessage: Pick<RelatedMessageType, 'id' | 'externalMessageRef' | 'type' | 'createdAt' | 'state'>;
  active: boolean;
};
type IconForMessageProps = Pick<RelatedMessageProps['relatedMessage'], 'state'> & { active: boolean };
const IconForMessage = forwardRef<HTMLDivElement, IconForMessageProps>(({ state, active }, ref) => {
  let icon = null;
  let classes;
  switch (state) {
    case TransactionState.success:
      icon = <CheckCircleIcon className="size-5" data-testid="CheckCircleIcon" />;
      classes = active ? 'text-procuros-green-600 border-procuros-green-200' : 'group-hover:text-procuros-green-500';
      break;
    case TransactionState.pending:
      icon = <InProgresCircle className="size-5" data-testid="InProgresCircle" />;
      classes = active ? 'text-yellow-600 border-yellow-200' : 'group-hover:text-yellow-500';
      break;

    case TransactionState.failed:
      icon = <ExclamationCircleIcon className="size-5" data-testid="ExclamationCircleIcon" />;
      classes = active ? 'text-red-600 border-red-200' : 'group-hover:text-red-500';
      break;

    case TransactionState.missing:
      icon = <DocumentMagnifyingGlassIcon className="size-5" data-testid="DocumentMagnifyingGlassIcon" />;
      classes = active ? 'text-yellow-600 border-yellow-200' : 'group-hover:text-yellow-500';
      break;

    case TransactionState.pending_review:
      icon = <DocumentCheckIcon className="size-5" data-testid="DocumentCheckIcon" />;
      classes = active ? 'text-sky-600 border-sky-200' : 'group-hover:text-sky-500';
      break;

    case TransactionState.dropped:
    case TransactionState.superseded:
    case TransactionState.unknown:
      icon = <XCircleIcon className="size-5" data-testid="XCircleIcon" />;
      classes = active ? 'text-gray-600 border-gray-200' : 'group-hover:text-gray-500';
      break;
  }

  return (
    <span className={classNames('inline-block relative text-gray-400', classes)} ref={ref}>
      {icon}
    </span>
  );
});

IconForMessage.displayName = 'IconForMessage';

export function RelatedMessage({ relatedMessage, active }: RelatedMessageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Prefetch the message with process specification
  useMessageWithProcessSpecification({ variables: { messageId: relatedMessage.id } });

  return (
    <li key={relatedMessage.id}>
      <div
        className={classNames(
          'flex items-center group -mx-2 px-2 py-1.5 rounded-md',
          active ? 'cursor-default bg-gray-100' : 'cursor-pointer',
        )}
        data-testid={`LinkToTransaction-${relatedMessage.id}`}
        onClick={() => {
          navigate(routeToTransactionPage(TransactionInternalType.message, relatedMessage.id), {
            state: { keepBreadcrumbs: true },
          });
        }}
      >
        <IconForMessage state={relatedMessage.state} active={active} />
        <div className="ml-2 w-full">
          <div className="grid w-full grid-cols-2 gap-1 text-xs">
            <span
              className={classNames(
                'self-center overflow-hidden text-ellipsis',
                active
                  ? 'text-gray-900 font-medium'
                  : 'text-gray-500 group-hover:text-gray-900 group-hover:font-medium',
              )}
            >
              {translationForDocumentTypeWithVirtual(t, relatedMessage.type)}
            </span>

            <span
              dir="rtl"
              className={classNames(
                'text-start m-auto whitespace-nowrap overflow-hidden text-ellipsis direction-rtl w-full',
                active
                  ? 'text-gray-900 font-medium'
                  : 'text-gray-500 group-hover:text-gray-900 group-hover:font-medium',
              )}
            >
              <Tooltip enabled={Boolean(relatedMessage.externalMessageRef)} placement="top-end">
                <TooltipTrigger>
                  <span>{relatedMessage.externalMessageRef || EMPTY_FIELD}</span>
                </TooltipTrigger>
                <TooltipContent>{relatedMessage.externalMessageRef}</TooltipContent>
              </Tooltip>
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}
